import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { toast } from 'sonner';

const defaultConfig = {
  recipients: [],
  successMessage: 'Thanks for reaching out! We will get back to you soon.',
  collectPhone: true
};

export default function ContactFormBuilder({
  teamId,
  sectionId,
  config = {},
  onConfigChange
}) {
  const mergedConfig = useMemo(() => ({ ...defaultConfig, ...(config || {}) }), [config]);
  const [recipientsInput, setRecipientsInput] = useState(mergedConfig.recipients.join(', '));
  const [successMessage, setSuccessMessage] = useState(mergedConfig.successMessage);
  const [collectPhone, setCollectPhone] = useState(Boolean(mergedConfig.collectPhone));
  const [loading, setLoading] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [submissionsLoading, setSubmissionsLoading] = useState(false);

  useEffect(() => {
    setRecipientsInput(mergedConfig.recipients.join(', '));
    setSuccessMessage(mergedConfig.successMessage);
    setCollectPhone(Boolean(mergedConfig.collectPhone));
  }, [mergedConfig]);

  useEffect(() => {
    if (!sectionId) return;
    loadSubmissions();
  }, [sectionId]);

  const loadSubmissions = async () => {
    if (!sectionId) return;
    setSubmissionsLoading(true);
    const { data, error } = await supabase
      .from('portal_contact_requests')
      .select('*')
      .eq('section_id', sectionId)
      .order('created_at', { ascending: false })
      .limit(10);

    if (error) {
      console.error('Error loading contact submissions:', error);
      toast.error('Failed to load recent submissions');
    } else {
      setSubmissions(data || []);
    }

    setSubmissionsLoading(false);
  };

  const handleSaveConfig = () => {
    if (!recipientsInput.trim()) {
      toast.error('Add at least one recipient email');
      return;
    }

    const recipients = recipientsInput
      .split(',')
      .map((email) => email.trim())
      .filter(Boolean);

    const invalidEmail = recipients.find((email) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
    if (invalidEmail) {
      toast.error(`Invalid email: ${invalidEmail}`);
      return;
    }

    onConfigChange({
      recipients,
      successMessage: successMessage.trim() || defaultConfig.successMessage,
      collectPhone
    });
    toast.success('Contact form updated');
  };

  const markResponded = async (submissionId) => {
    setLoading(true);
    try {
      const { error } = await supabase
        .from('portal_contact_requests')
        .update({ responded_at: new Date().toISOString() })
        .eq('id', submissionId);

      if (error) throw error;
      toast.success('Marked as responded');
      await loadSubmissions();
    } catch (error) {
      console.error('Error updating submission:', error);
      toast.error('Failed to update submission');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-3">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Recipient Emails</label>
          <input
            type="text"
            value={recipientsInput}
            onChange={(e) => setRecipientsInput(e.target.value)}
            placeholder="coach@example.com, manager@example.com"
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <p className="text-[11px] text-gray-500 mt-1">
            Separate multiple emails with commas. Each submission is emailed instantly.
          </p>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Success Message</label>
          <textarea
            value={successMessage}
            onChange={(e) => setSuccessMessage(e.target.value)}
            rows={2}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <label className="inline-flex items-center gap-2 text-xs text-gray-700">
          <input
            type="checkbox"
            checked={collectPhone}
            onChange={(e) => setCollectPhone(e.target.checked)}
            className="rounded border-gray-300"
          />
          Collect phone numbers (optional field for parents)
        </label>

        <div className="flex justify-end">
          <button
            onClick={handleSaveConfig}
            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Save Form Settings
          </button>
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium text-gray-800">Recent Submissions</h4>
          <button
            onClick={loadSubmissions}
            disabled={submissionsLoading}
            className="text-xs text-blue-600 hover:underline"
          >
            {submissionsLoading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>

        {submissions.length === 0 ? (
          <p className="text-xs text-gray-500">No submissions yet. Responses will appear here.</p>
        ) : (
          <div className="space-y-2">
            {submissions.map((submission) => (
              <div key={submission.id} className="border border-gray-200 rounded-lg p-3 bg-white">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-900 font-medium">
                    {submission.submitter_name || 'Anonymous'}
                  </p>
                  <p className="text-[11px] text-gray-500">
                    {new Date(submission.created_at).toLocaleString()}
                  </p>
                </div>
                <p className="text-xs text-gray-600 mt-1">{submission.submitter_email || 'No email provided'}</p>
                {submission.message && (
                  <p className="text-sm text-gray-700 mt-2 whitespace-pre-wrap">{submission.message}</p>
                )}
                {submission.responded_at ? (
                  <p className="text-[11px] text-green-600 mt-2">Responded {new Date(submission.responded_at).toLocaleString()}</p>
                ) : (
                  <button
                    onClick={() => markResponded(submission.id)}
                    disabled={loading}
                    className="mt-2 text-xs px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 disabled:opacity-50"
                  >
                    Mark Responded
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <p className="text-xs text-gray-500">
        Contact submissions are stored securely and emailed to your selected recipients. Use the refresh button to pull the latest entries.
      </p>
    </div>
  );
}

